import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { toast } from '@/lib/notifications';
import { reportNonFatal, serializeError } from '@/lib/errorReport';
import { broadcastToOwnersCallable } from '@/lib/callables';
import { BroadcastHistory } from '@/features/notifications/BroadcastHistory';
import { isPlatformHttpsLink } from '@etiquetachef/types';

const TITLE_MAX = 60;
const BODY_MAX = 200;

/**
 * Tela super-admin para disparar 1 push para todos os OWNERs da plataforma.
 *
 * Origem: sem issue — commit 0c4cf0c56, 2026-05-14 (a tela; o backend
 * `broadcastToOwners` veio minutos antes, em 8a9cc3079).
 *
 * Contrato: chama `broadcastToOwners` (Functions) e mostra no toast quantos
 * aparelhos o FCM aceitou e quantos recusou. Histórico abaixo vem do audit
 * (`BroadcastHistory`).
 *
 * Usado por: rota `notifications/broadcast` (`routes/router.tsx`), menu
 * Sistema (`AdminLayout`, rótulo "PUSH").
 *
 * Histórico:
 *  - 2026-09-28 16:51 +0200 — (#767) saiu o card "Sincronizar tópico"
 *    (`syncOwnersTopic`): o envio deixou de usar tópico FCM, então não há
 *    inscrição a reconciliar. O toast troca `messageId` pelas contagens.
 *  - 2026-09-28 17:57 +0200 — (#767) falha de envio reportada ao PostHog
 *    (`admin_broadcast_send_failed`), não só ao console.
 *  - 2026-10-10 13:39 +0200 — (#926) Origem apontava o commit do backend;
 *    "Usado por" citava um menu "Notificações" que não existe.
 *
 * Spec: `docs/screens/admin-notifications-broadcast.md`.
 */
export function NotificationsBroadcastPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const titleTrim = title.trim();
  const bodyTrim = body.trim();
  const linkTrim = link.trim();
  const titleValid = titleTrim.length >= 1 && titleTrim.length <= TITLE_MAX;
  const bodyValid = bodyTrim.length >= 1 && bodyTrim.length <= BODY_MAX;
  // Mesma regra do servidor (`BroadcastToOwnersInput.link`): só https (#767) e
  // só etiquetachef.com e subdomínios (#795 — o push vai com a marca a todos os
  // donos; link externo seria phishing pelo canal oficial).
  const linkValid = linkTrim.length === 0 || isPlatformHttpsLink(linkTrim);
  const canSubmit = titleValid && bodyValid && linkValid && !submitting;

  async function handleSend(): Promise<void> {
    setSubmitting(true);
    try {
      const res = await broadcastToOwnersCallable({
        title: titleTrim,
        body: bodyTrim,
        ...(linkTrim.length > 0 ? { link: linkTrim } : {}),
      });
      toast.success(
        t('notifications.broadcast.success', {
          // `?? '?'`: na janela de deploy a Function antiga ainda devolve
          // `{ messageId, sentAt }` e o toast sairia com lacunas (#767).
          owners: res.owners ?? '?',
          sent: res.sent ?? '?',
          failed: res.failed ?? '?',
        }),
      );
      setTitle('');
      setBody('');
      setLink('');
    } catch (err) {
      // PostHog (#767): em produção o console do navegador não chega até nós.
      reportNonFatal('admin_broadcast_send_failed', serializeError(err));
      toast.error(err instanceof Error ? err.message : t('notifications.broadcast.errorGeneric'));
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('notifications.broadcast.subtitle')}>
          {t('notifications.broadcast.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container
          header={
            <Header variant="h2" description={t('notifications.broadcast.form.cardHint')}>
              {t('notifications.broadcast.form.cardTitle')}
            </Header>
          }
        >
          <SpaceBetween size="m">
            <FormField
              label={t('notifications.broadcast.form.titleLabel')}
              constraintText={`${String(titleTrim.length)}/${String(TITLE_MAX)}`}
            >
              <Input
                value={title}
                onChange={({ detail }): void => {
                  setTitle(detail.value);
                }}
                placeholder={t('notifications.broadcast.form.titlePlaceholder')}
              />
            </FormField>

            <FormField
              label={t('notifications.broadcast.form.bodyLabel')}
              constraintText={`${String(bodyTrim.length)}/${String(BODY_MAX)}`}
            >
              <Textarea
                value={body}
                rows={4}
                onChange={({ detail }): void => {
                  setBody(detail.value);
                }}
                placeholder={t('notifications.broadcast.form.bodyPlaceholder')}
              />
            </FormField>

            <FormField
              label={t('notifications.broadcast.form.linkLabel')}
              errorText={!linkValid ? t('notifications.broadcast.form.linkInvalid') : undefined}
            >
              <Input
                type="url"
                value={link}
                onChange={({ detail }): void => {
                  setLink(detail.value);
                }}
                placeholder="https://app.etiquetachef.com/..."
              />
            </FormField>

            <Box float="right">
              <Button
                variant="primary"
                disabled={!canSubmit}
                onClick={(): void => {
                  setConfirmOpen(true);
                }}
              >
                {t('notifications.broadcast.form.sendButton')}
              </Button>
            </Box>
          </SpaceBetween>
        </Container>

        <BroadcastHistory />
      </SpaceBetween>

      <AdminConfirm
        visible={confirmOpen}
        title={t('notifications.broadcast.confirm.title')}
        confirmLabel={t('notifications.broadcast.confirm.confirmLabel')}
        cancelLabel={t('notifications.broadcast.confirm.cancelLabel')}
        loading={submitting}
        onConfirm={(): void => {
          void handleSend();
        }}
        onDismiss={(): void => {
          setConfirmOpen(false);
        }}
      >
        <Box variant="p">{t('notifications.broadcast.confirm.description')}</Box>
      </AdminConfirm>
    </ContentLayout>
  );
}
